import { useIntl } from 'react-intl';

import { useAppConfigContext } from '~/contexts/AppConfig';
import { Box } from '~/eds/src/components/Box/Box';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { NumberInput } from '~/eds/src/components/NumberInput/NumberInput';
import { CurrencySelect } from '~/eds/src/components/Select/subcomponents/CurrencySelect';
import { Nullable } from '~/types/Nullable';
import { createOnFilterOption } from '~/utils/select';

import { MonetaryValueCustomSettings } from '../types/fields';
import { WorkflowQuestionFormField } from './types';

export const MonetaryValueWorkflowQuestion = ({
  question,
  onChange,
  ...commonProps
}: WorkflowQuestionFormField<Nullable<string>>) => {
  const { isPanelEnlarged } = useAppConfigContext();
  const intl = useIntl();
  const { label, description, id, ...rest } = commonProps;
  const {
    field: { value, customSettings },
  } = question;
  const { currency_rule: currencyRule, currency_list: currencyList } =
    customSettings as MonetaryValueCustomSettings;

  const currencyLabel = intl.formatMessage(
    {
      id: 'MonetaryValueWorkflowQuestion.currencyLabel',
      defaultMessage: '{questionTitle} - Currency',
      description:
        'Monetary value question field in an intake form, the currency selection input. Example: questionTitle = "Contract Value"',
    },
    { questionTitle: label },
  );
  const amountLabel = intl.formatMessage(
    {
      id: 'MonetaryValueWorkflowQuestion.amountLabel',
      defaultMessage: '{questionTitle} - Amount',
      description:
        'Monetary value question field in an intake form, the amount input. Example: questionTitle = "Contract Value"',
    },
    { questionTitle: label },
  );

  const includeValues =
    currencyRule === 'specific' && currencyList ? currencyList : undefined;
  const excludeValues =
    currencyRule === 'any_except' && currencyList ? currencyList : undefined;

  return (
    <Layout
      direction={isPanelEnlarged ? 'row' : 'column'}
      spacing={isPanelEnlarged ? 2 : 8}
    >
      <Box flex={isPanelEnlarged ? '0 0 auto' : undefined}>
        <FormField
          {...rest}
          id={`${id}-currency`}
          name={`${id}-currency`}
          label={currencyLabel}
          description={description}
          input={CurrencySelect}
          value={value.currency}
          onChange={(val) => onChange(val, 'currency')}
          inputProps={{
            isMulti: false,
            enablePortal: true,
            width: isPanelEnlarged ? 'input.m.width' : '100%',
            onFilterOption: createOnFilterOption({
              includeValues,
              excludeValues,
            }),
          }}
        />
      </Box>
      <Box flex={isPanelEnlarged ? '0 0 auto' : undefined}>
        <FormField
          {...rest}
          id={`${id}-amount`}
          name={`${id}-amount`}
          label={amountLabel}
          input={NumberInput}
          value={value.amount}
          onChange={(val) => onChange(val, 'amount')}
          inputProps={{
            width: isPanelEnlarged ? 'input.m.width' : '100%',
          }}
        />
      </Box>
    </Layout>
  );
};
